import { useState, useEffect } from "react";
import { X, ArrowUp, ArrowDown, Eye, EyeSlash, ArrowCounterClockwise } from "@phosphor-icons/react";

// Reusable show/hide + reorder editor for the toolbar
export default function LayoutCustomizer({ open, onClose, items, layout, onSave, onReset, title = "Customize toolbar", subtitle = "Show, hide and reorder your toolbar icons.", testid = "toolbar-customizer" }) {
  const [order, setOrder] = useState([]);
  const [hidden, setHidden] = useState([]);

  useEffect(() => {
    if (!open) return;
    const savedOrder = layout?.order || [];
    const idx = (k) => { const i = savedOrder.indexOf(k); return i === -1 ? 999 : i; };
    const keys = [...items.map((i) => i.key)].sort((a, b) => idx(a) - idx(b));
    setOrder(keys);
    setHidden(layout?.hidden || []);
  }, [open, layout, items]);

  if (!open) return null;
  const titleOf = (k) => items.find((i) => i.key === k)?.title || k;
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= order.length) return;
    const arr = [...order]; [arr[i], arr[j]] = [arr[j], arr[i]]; setOrder(arr);
  };
  const toggle = (k) => setHidden((h) => h.includes(k) ? h.filter((x) => x !== k) : [...h, k]);
  const save = () => { onSave({ order, hidden }); onClose(); };
  const reset = () => { onReset(); onClose(); };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid={testid} onClick={(e) => e.stopPropagation()} className="relative bg-card border-2 border-black rounded-2xl w-full max-w-md hard-shadow p-5 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-lg font-bold">{title}</h3>
          <button data-testid="toolbar-customizer-close" onClick={onClose} className="press w-8 h-8 rounded-full border-2 border-black bg-white flex items-center justify-center"><X size={15} weight="bold" /></button>
        </div>
        <p className="text-xs text-black/50 mb-3">{subtitle}</p>
        <div className="flex-1 overflow-y-auto space-y-2">
          {order.map((k, i) => {
            const isHidden = hidden.includes(k);
            return (
              <div key={k} data-testid={`tb-item-${k}`} className={`flex items-center gap-2 rounded-xl border-2 border-black p-2.5 ${isHidden ? "bg-[#F3F4F6] opacity-60" : "bg-white"}`}>
                <span className="flex-1 font-bold text-sm truncate">{titleOf(k)}</span>
                <button data-testid={`tb-up-${k}`} onClick={() => move(i, -1)} disabled={i === 0} className="press w-8 h-8 rounded-lg border-2 border-black bg-white flex items-center justify-center disabled:opacity-30"><ArrowUp size={13} weight="bold" /></button>
                <button data-testid={`tb-down-${k}`} onClick={() => move(i, 1)} disabled={i === order.length - 1} className="press w-8 h-8 rounded-lg border-2 border-black bg-white flex items-center justify-center disabled:opacity-30"><ArrowDown size={13} weight="bold" /></button>
                <button data-testid={`tb-toggle-${k}`} onClick={() => toggle(k)} className="press w-8 h-8 rounded-lg border-2 border-black bg-white flex items-center justify-center">{isHidden ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}</button>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 pt-4">
          <button data-testid="toolbar-reset-btn" onClick={reset} className="press h-11 px-4 rounded-full border-2 border-black bg-white font-bold text-sm flex items-center gap-1"><ArrowCounterClockwise size={14} weight="bold" /> Reset</button>
          <button data-testid="toolbar-save-btn" onClick={save} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold border-2 border-black">Save</button>
        </div>
      </div>
    </div>
  );
}
